// FileUpload.tsx
import { useCallback, useState } from 'react';

interface Props {
  onUpload: (file: File, standard: string) => void;
  disabled: boolean;
}

export default function FileUpload({ onUpload, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [standard, setStandard] = useState('standard');

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleSubmit = () => {
    if (selectedFile) onUpload(selectedFile, standard);
  };

  return (
    <div className="max-w-2xl mx-auto p-8">
      <h1 className="text-3xl font-bold text-center mb-2">📋 AI 合同审查</h1>
      <p className="text-gray-500 text-center mb-8">上传合同文件，AI 自动识别风险条款</p>

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors
          ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".pdf,.docx,.doc,.txt"
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />
        {selectedFile ? (
          <div>
            <p className="text-lg font-medium">📄 {selectedFile.name}</p>
            <p className="text-sm text-gray-500 mt-1">
              {(selectedFile.size / 1024).toFixed(1)} KB
            </p>
          </div>
        ) : (
          <div>
            <p className="text-5xl mb-4">📁</p>
            <p className="text-lg text-gray-600">拖拽文件到此处，或点击选择</p>
            <p className="text-sm text-gray-400 mt-2">支持 PDF、DOCX、TXT 格式</p>
          </div>
        )}
      </div>

      {/* Review standard selector */}
      {selectedFile && (
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">审查标准</label>
            <div className="flex gap-3">
              {[
                { value: 'strict', label: '🔴 严格', desc: '标记所有可能风险' },
                { value: 'standard', label: '🟡 标准', desc: '标记实质性风险' },
                { value: 'lenient', label: '🟢 宽松', desc: '仅标记重大风险' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setStandard(opt.value)}
                  className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors
                    ${standard === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={disabled}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium
              hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {disabled ? '审查中...' : '开始审查'}
          </button>
        </div>
      )}
    </div>
  );
}